import React from 'react';
import PropTypes from 'prop-types';
import { theme } from '../../tailwind.config';
import { Title, Body, Caption } from '../styled components/Typography';
import Chip from '../components/Chip/Chip';
import Icon from '../components/SVGIcon/SVGIcon';
import UICard from '../styled components/UICard';
import { UIBar, UIBarGrowItem } from '../styled components/UIBar';
import Button from '../components/Button/Button';
import { setScrollPositionAppContainer } from '../libs/fixDefaultScrollposition';
import debounce from '../libs/debounce';
import getUserDefaultTabHash from '../libs/userUtilities';
import FormatSettingsContext from '../components/FormatSettingsContext';
import YPEventObject from '../yesplan_classes/YPEventObject';
import NavigationHistory from '../libs/NavigationHistory';
import { capture, PH_EVENT_DETAIL_CLICKED } from '../libs/posthogAnalytics';

class EventInfoCard extends React.Component {
  static profileBorderColor = 'rgba(0,0,0,0.85)';

  static statusBackgroundColor = 'rgb(240,240,240)';

  static statusTextColor = 'rgba(0,0,0,0.85)';

  constructor(props) {
    super(props);
    this.handleEventClick = debounce(this.handleEventClick.bind(this));
  }

  handleEventClick(event) {
    const { eventInfo, linkUrlFor, user } = this.props;
    event.stopPropagation();
    setScrollPositionAppContainer(0);
    capture(PH_EVENT_DETAIL_CLICKED, {
      eventId: eventInfo.id,
      eventTitle: eventInfo.name,
      openedOnTab: getUserDefaultTabHash(user).slice(1),
      clickedInContextOf: 'events list'
    });
    this.props.navigationHistory.goTo({
      pathname: linkUrlFor(`/event/${eventInfo.id}`),
      state: { headerTitle: eventInfo.name },
      hash: getUserDefaultTabHash(user)
    });
  }

  renderDateTime(eventInfo) {
    const { showDate } = this.props;
    const showTime = this.props.showTime
      ? eventInfo.printDateAndTimeString(
          false,
          this.context.timeFormatSetting,
          this.context.dateFormatSetting
        )
      : this.props.showTime;
    if (showTime || showDate) {
      return (
        <div tw="flex space-x-4" data-test-event-date-time>
          <Icon icon="clock" size="small" tw="mt-4 text-black text-opacity-60" />
          <div tw="flex">
            {showDate && (
              <Body $color="black" tw="font-bold ">
                {eventInfo.printDateString(this.context.dateFormatSetting)}
              </Body>
            )}

            {showTime && (
              <Body $color="gray dark">
                {showDate && ','}
                {eventInfo.printDateAndTimeString(
                  false,
                  this.context.timeFormatSetting,
                  this.context.dateFormatSetting
                )}
              </Body>
            )}
          </div>
        </div>
      );
    }
    return '';
  }

  renderStatusChip(eventInfo) {
    const { statuses } = this.props;
    const status = statuses[eventInfo.status.id];
    const bgColor =
      status && status.backgroundcolor
        ? status.backgroundcolor
        : this.statusBackgroundColor;
    const textColor =
      status && status.textcolor ? status.textcolor : this.statusTextColor;

    return (
      <Chip tw="mr-8" bgColor={bgColor} borderColor={bgColor} textColor={textColor}>
        <Caption>{eventInfo.printStatusString()}</Caption>
      </Chip>
    );
  }

  renderGroupNames(eventInfo) {
    if (eventInfo.isPartOfGroup()) {
      const groupName = `${eventInfo.getDirectGroupNameOrPlaceholder()}`;
      return eventInfo.hasTopGroup()
        ? `${eventInfo.getTopGroupNameOrPlaceholder()} - ${groupName}`
        : groupName;
    }
    return null;
  }

  renderProfileChip(eventInfo) {
    const { profiles } = this.props;
    const profile = profiles[eventInfo.profile.id];
    const borderColor =
      profile && profile.color ? profile.color : this.profileBorderColor;

    return (
      <Chip tw="mr-8" bgColor={theme.colors.white} borderColor={borderColor}>
        <Caption>{eventInfo.printProfileString()}</Caption>
      </Chip>
    );
  }

  render() {
    const { eventInfo, showGroup } = this.props;
    const dataTestEventId = `eventInfo-${eventInfo.id}`;
    return (
      <div
        tw="w-full"
        data-test-type="eventInfo"
        data-test-eventid={dataTestEventId}
        data-js-eventinfo-card
      >
        {/* event info */}
        <UICard onClick={this.handleEventClick}>
          <div tw="flex space-x-16 pt-0 pl-16 pb-12 pr-2">
            <div tw="w-full flex-auto min-w-0">
              {/* event name */}
              <div tw="w-full cursor-pointer transition opacity-100 active:opacity-50">
                <UIBar tw="items-start">
                  <Title tw="mt-8 mb-8 block">
                    {eventInfo.eventNameOrPlaceholder()}
                  </Title>
                  <UIBarGrowItem />
                  <Button
                    contentType="icon"
                    icon="navigate-next"
                    size="medium"
                    tw="mt-2 -mb-12"
                    onClick={this.handleEventClick}
                  />
                </UIBar>
              </div>
              {/* date & time */}
              {this.renderDateTime(eventInfo)}

              {/* location */}
              <div tw="space-x-4 flex">
                <Icon
                  icon="map-marker"
                  size="small"
                  tw="mt-4 text-black text-opacity-60"
                />
                <Body $color="gray dark">{eventInfo.printLocationsString()}</Body>
              </div>
              {/* event group */}
              {showGroup && eventInfo.isPartOfGroup() && (
                <div tw="space-x-4 flex">
                  <Icon
                    icon="tree-view"
                    size="small"
                    tw="mt-4 text-black text-opacity-60"
                  />
                  <Body $color="gray dark">{this.renderGroupNames(eventInfo)}</Body>
                </div>
              )}
              <div tw="mt-4 max-w-full">
                {/* status */}
                {this.renderStatusChip(eventInfo)}
                {/* profile */}
                {this.renderProfileChip(eventInfo)}
              </div>
            </div>
          </div>
        </UICard>
      </div>
    );
  }
}

EventInfoCard.propTypes = {
  eventInfo: PropTypes.instanceOf(YPEventObject).isRequired,
  linkUrlFor: PropTypes.func.isRequired,
  navigationHistory: PropTypes.instanceOf(NavigationHistory).isRequired,
  showDate: PropTypes.bool,
  /** If true, time will only be shown if there's a set start time  */
  showTime: PropTypes.bool,
  showGroup: PropTypes.bool,
  statuses: PropTypes.shape({}).isRequired,
  profiles: PropTypes.shape({}).isRequired,
  user: PropTypes.shape({
    preferences: PropTypes.shape({
      defaultEventInspectorTabApp: PropTypes.shape({})
    })
  }).isRequired
};

EventInfoCard.defaultProps = {
  showDate: false,
  showTime: true,
  showGroup: false
};

EventInfoCard.contextType = FormatSettingsContext;

export default EventInfoCard;
